/**
 * Custom hook that owns the Save side of the Auth Letter Upload modal:
 * validating the full {@link AuthLetterUploadFormState} model, advancing
 * tabs, and posting the authorizations to Candid on the last tab.
 *
 * The form states are the authoritative tab records, so there is nothing to
 * sync on save — Save either advances to the next tab or uploads them as-is.
 */
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useMemo, useState } from "react";

import { ToastMessages } from "../../../../components/toasts/toastMessages";
import { AuthLetterUploadFormState } from "../../../../data-model/candidAuthorizationModel";
import { CandidAuthorizationsResponse } from "../../../../data-model/candidAuthorizationsResponse";

/**
 * String fields that must be non-blank on every tab.
 * Notes and NPI billing number are omitted so they stay optional on Save.
 */
const REQUIRED_STRING_FIELDS: (keyof AuthLetterUploadFormState)[] = [
  "authorizationNumber",
  "payerName",
  "startDate",
  "endDate",
  "quantity",
  "units",
];

/** Tag-list fields that need at least one non-blank entry. */
const REQUIRED_TAG_FIELDS: (keyof AuthLetterUploadFormState)[] = [
  "serviceCodes",
  "dx_codes",
];

interface UseAuthSaveFlowProps {
  /** Authorization tabs currently in the modal. */
  formStates: AuthLetterUploadFormState[];
  /** Index of the active authorization tab. */
  selectedIndex: number;
  /**
   * Unlocks and selects the next sidebar tab.
   *
   * @param totalLength - Number of tabs after the current save
   */
  advanceToNextTab: (totalLength: number) => void;
  /**
   * Posts the authorizations to Candid on the final tab. Rejections surface
   * through `uploadError` and an error toast.
   *
   * @param authData - Authorization tabs to upload
   * @returns Written-authorizations envelope from coverage-service
   */
  onUpload: (
    authData: AuthLetterUploadFormState[],
  ) => Promise<CandidAuthorizationsResponse>;
  /**
   * Called after a successful POST. The caller refetches order data, closes
   * the modal, and shows a toast from the write-back result.
   *
   * @param result - Authorizations POST envelope
   */
  onSuccess: (
    result: CandidAuthorizationsResponse,
  ) => void | Promise<void>;
}

/**
 * Returns true when any required field on the form model is empty.
 *
 * @param form - One authorization tab's form snapshot
 * @returns Whether that tab is incomplete
 */
const isFormIncomplete = (form: AuthLetterUploadFormState): boolean => {
  const hasEmptyString = REQUIRED_STRING_FIELDS.some((key) => {
    const value = form[key];
    return typeof value !== "string" || !value.trim();
  });
  if (hasEmptyString) {
    return true;
  }
  return REQUIRED_TAG_FIELDS.some((key) => {
    const tags = form[key];
    return (
      !Array.isArray(tags) ||
      tags.length === 0 ||
      tags.every((tag) => !tag.trim())
    );
  });
};

/**
 * Save-flow orchestration: validate the active tab, then advance or upload.
 *
 * @param props.formStates - Authorization tabs currently in the modal
 * @param props.selectedIndex - Index of the active authorization tab
 * @param props.advanceToNextTab - Navigation hook helper
 * @param props.onUpload - Async upload trigger
 * @param props.onSuccess - Called after a successful POST
 * @returns Save button state, upload error, and handlers
 */
export const useAuthSaveFlow = ({
  formStates,
  selectedIndex,
  advanceToNextTab,
  onUpload,
  onSuccess,
}: UseAuthSaveFlowProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const isLastTab = selectedIndex === formStates.length - 1;

  /**
   * Awaits `onUpload`, keeping Save disabled for the duration and recording
   * any failure so the modal can show it inline. Invokes `onSuccess` after a
   * successful POST.
   *
   * @param authData - Authorization tabs to upload
   */
  const runUpload = useCallback(
    async (authData: AuthLetterUploadFormState[]) => {
      setIsUploading(true);
      setUploadError(null);
      try {
        const result = await onUpload(authData);
        await onSuccess(result);
      } catch (error) {
        setUploadError(
          error instanceof Error
            ? error.message
            : ToastMessages.CANDID_WRITEBACK_ERROR,
        );
        toast.error(ToastMessages.CANDID_WRITEBACK_ERROR);
      } finally {
        setIsUploading(false);
      }
    },
    [onUpload, onSuccess],
  );

  /**
   * Advances to the next tab, or posts all tabs to Candid when already on the last.
   */
  const handleSave = useCallback(() => {
    if (isLastTab) {
      void runUpload(formStates);
      return;
    }
    advanceToNextTab(formStates.length);
  }, [isLastTab, formStates, advanceToNextTab, runUpload]);

  const isSaveDisabled = useMemo(() => {
    if (isUploading || formStates.length === 0) {
      return true;
    }
    if (isLastTab) {
      return formStates.some(isFormIncomplete);
    }
    const current = formStates[selectedIndex];
    return !current || isFormIncomplete(current);
  }, [isUploading, formStates, selectedIndex, isLastTab]);

  const saveButtonText = isLastTab ? "Upload" : "Save & Next";

  return {
    isSaveDisabled,
    saveButtonText,
    handleSave,
    isUploading,
    uploadError,
  };
};
